/**
 * 掲示板の標準投稿テンプレート（活用シーン別・10種）。
 * 「テンプレートから選択」モーダルに常時表示される組み込みプリセット。
 * 本文は TipTap 互換 HTML。【 】は投稿者が埋める差し込み箇所。
 *
 * ユーザーが追加したカスタムテンプレートは Firestore (board_templates) に保存され、
 * これらの組み込みテンプレートと一緒にモーダルへ並ぶ（useBoardTemplates）。
 */
import type { BoardPostType, BoardUrgency } from '@goenhub/shared';

export interface BuiltinBoardTemplate {
  /** 組み込みテンプレートの安定 id（'builtin:' プレフィックス）。 */
  id: string;
  /** テンプレート名（活用シーン）。 */
  name: string;
  /** ひとことの用途説明。 */
  description: string;
  type: BoardPostType;
  urgency: BoardUrgency;
  subject: string;
  bodyHtml: string;
}

export const BUILTIN_BOARD_TEMPLATES: readonly BuiltinBoardTemplate[] = [
  {
    id: 'builtin:decision-approval',
    name: '施策・方針の承認依頼',
    description: '新しい施策や方針の実施可否を関係者に承認してもらう',
    type: 'decision',
    urgency: 'medium',
    subject: '【承認依頼】〇〇の実施について',
    bodyHtml:
      '<p>お疲れさまです。下記の施策について実施可否のご判断をお願いします。</p>' +
      '<h3>内容</h3><p>【何を・なぜ実施したいか】</p>' +
      '<h3>期待効果</h3><ul><li>【効果1】</li><li>【効果2】</li></ul>' +
      '<h3>想定コスト・リスク</h3><p>【費用 / 工数 / 懸念点】</p>' +
      '<p><strong>期日までに 決定 / 却下 / 保留 をご回答ください。</strong></p>',
  },
  {
    id: 'builtin:decision-quote',
    name: '見積・金額の承認',
    description: '発注や見積金額の承認を仰ぐ（金額が絡む決裁）',
    type: 'decision',
    urgency: 'high',
    subject: '【金額承認】〇〇の見積（¥〇〇）',
    bodyHtml:
      '<p>下記の見積について発注可否のご承認をお願いします。</p>' +
      '<h3>対象</h3><p>【商品 / 外注 / 仕入 など】</p>' +
      '<h3>金額</h3><p>税抜 ¥【金額】（税込 ¥【金額】）／ 支払条件：【条件】</p>' +
      '<h3>取引先</h3><p>【メーカー / 業者名】</p>' +
      '<p>問題なければ「決定」、再検討が必要なら「却下」「保留」をお願いします。</p>',
  },
  {
    id: 'builtin:task-request',
    name: '作業依頼（担当・期日つき）',
    description: '具体的なタスクを担当者に依頼する',
    type: 'task',
    urgency: 'medium',
    subject: '【作業依頼】〇〇の対応をお願いします',
    bodyHtml:
      '<p>下記の作業対応をお願いします。</p>' +
      '<h3>依頼内容</h3><p>【やってほしいこと】</p>' +
      '<h3>ゴール・完了条件</h3><ul><li>【完了の基準】</li></ul>' +
      '<h3>参考情報</h3><p>【資料 / URL / 補足】</p>' +
      '<p>完了したら「完了」、着手が難しい場合は「保留」「キャンセル」でお知らせください。</p>',
  },
  {
    id: 'builtin:task-bug',
    name: '不具合・障害の対応依頼',
    description: 'システム不具合や障害の調査・復旧を依頼する',
    type: 'task',
    urgency: 'high',
    subject: '【至急】〇〇の不具合対応',
    bodyHtml:
      '<p>下記の不具合が発生しています。対応をお願いします。</p>' +
      '<h3>症状</h3><p>【何が起きているか】</p>' +
      '<h3>発生条件・再現手順</h3><ol><li>【手順1】</li><li>【手順2】</li></ol>' +
      '<h3>影響範囲</h3><p>【誰が / どの機能が影響を受けるか】</p>' +
      '<h3>暫定対応</h3><p>【あれば記載 / なければ「なし」】</p>' +
      '<p>対応状況を「完了」「保留」でご共有ください。</p>',
  },
  {
    id: 'builtin:info-announcement',
    name: '業務連絡・お知らせ',
    description: '一般的な社内連絡・周知事項',
    type: 'info',
    urgency: 'low',
    subject: '【お知らせ】〇〇について',
    bodyHtml:
      '<p>お疲れさまです。下記のとおりお知らせします。</p>' +
      '<p>【連絡内容】</p>' +
      '<h3>対応が必要なこと</h3><ul><li>【あれば記載】</li></ul>' +
      '<p>ご不明点があればスレッドでご質問ください。</p>',
  },
  {
    id: 'builtin:info-spec-change',
    name: '新機能・仕様変更の周知',
    description: 'システムの新機能追加や仕様変更を全員に共有する',
    type: 'info',
    urgency: 'medium',
    subject: '【仕様変更】〇〇の機能を更新しました',
    bodyHtml:
      '<p>下記のとおり機能を更新しましたので共有します。</p>' +
      '<h3>変更点</h3><ul><li>【変更1】</li><li>【変更2】</li></ul>' +
      '<h3>使い方・注意点</h3><p>【操作方法 / 影響 / 移行の注意】</p>' +
      '<h3>反映日</h3><p>【yyyy/mm/dd より】</p>' +
      '<p>気になる点があればスレッドへお願いします。</p>',
  },
  {
    id: 'builtin:info-schedule',
    name: '休業・スケジュールのお知らせ',
    description: '休業日や全社スケジュールを案内する',
    type: 'info',
    urgency: 'low',
    subject: '【お知らせ】〇〇期間の休業について',
    bodyHtml:
      '<p>下記の期間を休業とします。</p>' +
      '<h3>休業期間</h3><p>【yyyy/mm/dd 〜 yyyy/mm/dd】</p>' +
      '<h3>期間中の対応</h3><ul><li>緊急連絡：【連絡先 / フロー】</li><li>メール自動返信：【設定有無】</li></ul>' +
      '<p>ご確認をお願いします。</p>',
  },
  {
    id: 'builtin:task-review',
    name: '資料・データの確認依頼',
    description: '作成した資料や数値のダブルチェックを依頼する',
    type: 'task',
    urgency: 'medium',
    subject: '【確認依頼】〇〇のチェックをお願いします',
    bodyHtml:
      '<p>下記の内容の確認をお願いします。</p>' +
      '<h3>確認対象</h3><p>【資料 / 請求書 / 数値 など】</p>' +
      '<h3>確認してほしい点</h3><ul><li>【観点1】</li><li>【観点2】</li></ul>' +
      '<h3>期限</h3><p>【yyyy/mm/dd まで】</p>' +
      '<p>問題なければ「完了」、修正が必要なら「保留」でスレッドにご指摘ください。</p>',
  },
  {
    id: 'builtin:decision-customer',
    name: '顧客対応方針の相談',
    description: '顧客対応やクレームへの方針を相談・決定する',
    type: 'decision',
    urgency: 'medium',
    subject: '【相談】〇〇様への対応方針について',
    bodyHtml:
      '<p>下記の顧客対応について方針のご判断をお願いします。</p>' +
      '<h3>状況</h3><p>【経緯 / 顧客の要望・不満】</p>' +
      '<h3>対応案</h3><ul><li>案A：【内容】</li><li>案B：【内容】</li></ul>' +
      '<h3>希望・懸念</h3><p>【こちらの意向 / リスク】</p>' +
      '<p>採用する案について「決定」、別案が良ければ「却下」「保留」をお願いします。</p>',
  },
  {
    id: 'builtin:info-minutes',
    name: '議事録・打ち合わせメモの共有',
    description: '打ち合わせの決定事項とToDoを共有する',
    type: 'info',
    urgency: 'low',
    subject: '【議事録】〇〇打ち合わせ（yyyy/mm/dd）',
    bodyHtml:
      '<p>本日の打ち合わせ内容を共有します。</p>' +
      '<h3>参加者</h3><p>【氏名】</p>' +
      '<h3>決定事項</h3><ul><li>【決定1】</li><li>【決定2】</li></ul>' +
      '<h3>ToDo（担当 / 期日）</h3><ul><li>【タスク】— 担当：【氏名】 期日：【日付】</li></ul>' +
      '<h3>次回</h3><p>【日時 / 議題】</p>',
  },
] as const;
